import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Badge } from "@circleco/compass/components/Badge";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { getAvatarStatus } from "@/react/components/Spaces/ChatSpace/RailBar/ParticipantList/helper";
import { ParticipantActionsDropdownComponent } from "@/react/components/Spaces/ChatSpace/RailBar/ParticipantList/ParticipantActionsDropdownComponent";
import { useBlockDms } from "@/react/hooks/chatsV2/blockDms/useBlockDms";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";

import type { ParticipantListProps } from "./types";

export const ParticipantListComponent = ({
  currentParticipant,
  toggleBlockDmsModal,
  handleMakeAdmin,
  onRemoveParticipant,
  onUnblockMemberDm,
  participantList,
  showMemberProfile,
}: ParticipantListProps) => {
  const { canBlockMemberDm } = useBlockDms();
  const shouldShowDropdown = (participant: ChatRoomParticipant) => {
    const isCurrentUser = currentParticipant.id === participant.id;
    return !isCurrentUser && currentParticipant.admin;
  };
  const getProfileActionItems = (
    participant: ChatRoomParticipant,
  ): MenuOptions => {
    const isBlocked = participant.is_blocked_by_current_community_member;
    const profileActionItems: MenuOptions = [
      {
        icon: "eye-open",
        label: t("view_profile"),
        onClick: () => showMemberProfile(participant.user_public_uid || ""),
      },
      {
        icon: "people-circle",
        label: participant.admin
          ? t("messaging.remove_admin")
          : t("messaging.make_admin"),
        onClick: () => {
          void handleMakeAdmin({
            participantId: participant.id,
            status: !participant.admin,
          });
        },
      },
    ];

    if (
      canBlockMemberDm({
        admin: participant.community_admin,
        community_member_id: participant.community_member_id,
      })
    ) {
      profileActionItems.push({
        icon: isBlocked ? "message-check" : "message-cross",
        label: {
          blocked: () => t("messaging.unblock_direct_messages"),
          unblocked: () => t("messaging.block_direct_messages"),
        }[isBlocked ? "blocked" : "unblocked"](),
        onClick: () =>
          isBlocked
            ? onUnblockMemberDm(participant)
            : toggleBlockDmsModal(participant),
        danger: !isBlocked,
      });
    }

    profileActionItems.push({
      icon: "cross",
      label: t("messaging.remove"),
      onClick: () => {
        void onRemoveParticipant({ participantId: participant.id });
      },
      danger: true,
    });

    return profileActionItems;
  };

  return (
    <div className="flex flex-col gap-0.5 -mx-2">
      {participantList.map(participant => {
        const isCurrentUser = currentParticipant.id === participant.id;
        const participantButton = (
          <button
            type="button"
            className="hover:bg-hover dark:hover:bg-primary focus-visible:bg-hover has-[[data-popup-open]]:bg-hover dark:has-[[data-popup-open]]:bg-primary group flex h-10 w-full cursor-pointer flex-row items-center justify-between rounded-2xl px-3 py-2"
            aria-label={t("messaging.open_member_profile_button_aria_label", {
              name: participant.name,
            })}
            onClick={() => showMemberProfile(participant.user_public_uid || "")}
          >
            <div
              className={classNames("flex w-[90%] items-center text-left", {
                "opacity-50": participant.messaging_enabled === false,
              })}
            >
              <div className="mr-3">
                <Avatar
                  name={participant.name}
                  src={participant.avatar_url}
                  status={getAvatarStatus(participant.status)}
                  size="sm"
                />
              </div>
              <Typography variant="label-sm" truncate>
                {isCurrentUser ? t("messaging.you") : participant.name}
              </Typography>
              {participant.admin && (
                <div className="ml-2">
                  <Badge label={t("moderator")} variant="secondary" />
                </div>
              )}
            </div>
            {shouldShowDropdown(participant) && (
              <ParticipantActionsDropdownComponent
                options={getProfileActionItems(participant)}
              />
            )}
          </button>
        );

        if (participant.messaging_enabled === false) {
          return (
            <Tooltip
              key={participant.id}
              content={t("messaging.member_disabled_messaging")}
            >
              {participantButton}
            </Tooltip>
          );
        }

        return <div key={participant.id}>{participantButton}</div>;
      })}
    </div>
  );
};
